import { Platform } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { notifySuccess } from '@/constants/utils/haptics';

/**
 * Cross-platform utility to copy text to clipboard with optional HTML content
 * @param {string} text - The text to copy
 * @param {string} [htmlContent] - Optional HTML content for rich text copying
 * @returns {Promise<void>}
 */
const copyText = async (text, htmlContent) => {
  if (Platform.OS === 'web') {
    // Try modern clipboard API first
    if (navigator.clipboard) {
      if (htmlContent) {
        try {
          // Try rich text copying first
          const clipboardItem = new ClipboardItem({
            'text/html': new Blob([htmlContent], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' })
          });
          await navigator.clipboard.write([clipboardItem]);
          return;
        } catch (err) {
          console.warn('Rich text copy failed, falling back to plain text:', err);
        }
      }
      
      // Fallback to plain text
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (err) {
        console.warn('Clipboard API failed, falling back to execCommand:', err);
      }
    }

    // Final fallback using execCommand
    const container = document.createElement('div');
    if (htmlContent) {
      container.innerHTML = htmlContent;
      container.style.position = 'fixed';
      container.style.pointerEvents = 'none';
      container.style.opacity = '0';
      document.body.appendChild(container);

      // Create a selection range
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(container);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Fallback for plain text
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.opacity = '0';
      container.textContent = text;
      document.body.appendChild(container);
      
      const selection = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(container);
      selection.removeAllRanges();
      selection.addRange(range);
    }

    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
      throw new Error('Copy failed');
    } finally {
      if (container.parentNode) {
        document.body.removeChild(container);
      }
      // Clear selection if any
      window.getSelection().removeAllRanges();
    }
  } else {
    await Clipboard.setStringAsync(text);
    notifySuccess();
  }
};

export default copyText; 